<template>
  <a href="#" class="rcard tu-browse-card" :aria-label="`View path: ${pathway.title}`" @click.prevent="$emit('view', pathway)">
    <!-- name + route on the left, the path photo on the right -->
    <div class="rc-head">
      <div class="rc-headtext">
        <h3 class="rc-title" :title="pathway.title">{{ pathway.title }}</h3>
        <p v-if="pathway.subtitle" class="rc-sub">{{ pathway.subtitle }}</p>
        <span v-if="ends" class="rc-route" aria-label="From and to credential">
          <span class="tu-credential-badge tu-credential-badge--start rc-chip">{{ ends.start }}</span>
          <svg class="rc-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5"/></svg>
          <template v-if="mid">
            <span class="tu-credential-badge rc-chip">{{ mid }}</span>
            <svg class="rc-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5"/></svg>
          </template>
          <span class="tu-credential-badge rc-chip">{{ ends.end }}</span>
        </span>
      </div>
      <span class="rc-tile" aria-hidden="true">
        <img v-if="pathway.image_url" :src="pathway.image_url" alt="" loading="lazy" />
        <md-icon v-else>directions</md-icon>
      </span>
    </div>
    <span v-if="enrolled" class="rc-enrolled"><md-icon>check_circle</md-icon> Enrolled</span>

    <p v-if="pathway.description" class="rc-desc">{{ pathway.description }}</p>

    <!-- the facts a reader compares on -->
    <dl v-if="time || programs" class="rc-facts">
      <div v-if="programs"><dt>Programs</dt><dd>{{ $n(programs) }}<span v-if="stateLabel" class="rc-facts-state">{{ stateLabel }}</span></dd></div>
      <div><dt>Prereqs</dt><dd>10</dd></div>
      <div v-if="time"><dt>Timeline</dt><dd>{{ time }}</dd></div>
    </dl>

    <span class="rc-foot">
      <span class="tu-card-action rc-go"><span class="tu-card-action-label">View path</span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></span>
    </span>
  </a>
</template>

<script>
import { get } from 'lodash';
import { pathwayEnds, pathwayTime, stateAbbr } from '@/common/tuPathway';

/**
 * Pathway card for the student portal. The same card as transitionu.health
 * (components/Tu/PathwayCard.vue): title and subtitle, the path photo, the credential
 * route, a two line description, a Timeline / Programs panel, one "View path" action.
 */
export default {
  name: 'PathwayCard',
  props: {
    pathway: { type: Object, required: true },
    enrolled: { type: Boolean, default: false },
    // Program count for the path (the list resolves it from the directory). Falls back to attached programs.
    schools: { type: Number, default: null },
  },
  computed: {
    ends() {
      return pathwayEnds(this.pathway);
    },
    mid() {
      const m = get(this.pathway, 'mid_program');
      return (m && (m.abbr || m.title)) || '';
    },
    time() {
      return pathwayTime(this.pathway);
    },
    programs() {
      if (this.schools != null) return this.schools;
      const uops = get(this.pathway, 'uni_offered_programs');
      if (!Array.isArray(uops) || !uops.length) return 0;
      return uops.length;
    },
    stateLabel() {
      const s = get(this.pathway, 'state');
      return s ? stateAbbr(s) : 'All states';
    },
  },
};
</script>

<style lang="scss" scoped>
.rcard {
  display: flex;
  flex-direction: column;
  background: var(--tu-surface);
  border: 1px solid var(--tu-card-border, rgba(24, 24, 24, 0.1));
  border-radius: 16px;
  padding: 24px;
  height: 100%;
  min-width: 0;
  box-sizing: border-box;
  text-decoration: none !important;
  color: var(--tu-ink) !important;
  box-shadow: none;
}

.rc-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; min-width: 0; min-height: 76px; }
.rc-tile {
  flex: none; width: 76px; height: 76px; border-radius: 14px; overflow: hidden;
  background: var(--tu-blue-tint); color: var(--tu-blue-deep);
  display: inline-flex; align-items: center; justify-content: center;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .md-icon { font-size: 30px; color: var(--tu-blue-deep); }
}
.rc-headtext { min-width: 0; padding-top: 2px; }
/* chips under the title and subtitle, like edunurse.com */
.rc-title {
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  font-family: var(--tu-font-text); font-weight: 700; font-size: 22px; letter-spacing: -0.015em; line-height: 1.18; color: var(--tu-ink); margin: 0;
}
.rc-sub { margin: 4px 0 0; font-size: 14px; font-weight: 500; line-height: 1.3; color: var(--tu-ink-body, var(--tu-ink)); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.rc-routewrap { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-top: 18px; min-height: 36px; }
.rc-route { display: inline-flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.rc-route .rc-chip { white-space: nowrap; font-size: 13px; font-weight: 700; padding: 6px 10px; border-radius: 8px; letter-spacing: 0.01em; line-height: 1.2; }
.rc-arr { width: 15px; height: 15px; color: var(--tu-ink-mute); flex: none; }
.rc-enrolled {
  align-self: flex-start; margin-top: 10px;
  display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px 3px 7px; border-radius: 999px;
  background: var(--tu-green-tint); color: var(--tu-green); font-size: 11px; font-weight: 700; letter-spacing: 0.02em; line-height: 1.4; white-space: nowrap;
  .md-icon { font-size: 13px; width: 13px; height: 13px; min-width: 0; color: var(--tu-green); }
}

.rc-desc { margin: 16px 0 0; font-size: 15px; line-height: 1.55; color: var(--tu-ink-soft); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

/* facts: a light blue panel */
.rc-facts { display: grid; grid-template-columns: auto auto auto; justify-content: space-between; gap: 12px 16px; margin: 18px 0 16px; padding: 16px 20px; border-radius: 14px; background: var(--tu-blue-tint); }
.rc-facts > div { min-width: 0; }
.rc-facts dt { margin: 0; font-size: 13px; font-weight: 500; color: var(--tu-ink-soft); }
.rc-facts dd { margin: 6px 0 0; font-family: var(--tu-font-text); font-weight: 700; font-size: 20px; line-height: 1.2; letter-spacing: -0.01em; color: var(--tu-ink); }
.rc-facts-state { display: block; margin-top: 2px; font-weight: 500; font-size: 13px; color: var(--tu-ink-soft); }
.rc-facts dd { white-space: nowrap; }
.rc-headtext .rc-route { display: inline-flex; margin-top: 12px; }
@media (max-width: 420px) { .rc-facts dd { font-size: 17px; } }

.rc-foot { display: flex; align-items: center; margin-top: auto; padding-top: 16px; border-top: 1px solid var(--tu-line-soft); }
.rc-foot .rc-go { margin: 0; padding: 0; border: 0; width: 100%; min-height: 0; justify-content: center; gap: 6px; }
.rc-go > svg { transition: transform 0.16s ease; }
.rcard:hover .rc-go > svg { transform: translateX(2px); }

@media (prefers-reduced-motion: reduce) {
  .rcard, .rc-go > svg { transition: none; }
  .rcard:hover, .rcard:hover .rc-go > svg { transform: none; }
}
</style>
